import { Component } from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Per-route error boundary.
 *
 * Wraps the routed page so that a runtime exception inside any single page
 * surfaces a friendly recovery panel instead of blanking the entire app
 * (sidebar + topbar stay intact). The boundary resets on route change.
 *
 * Usage:
 *   <RouteErrorBoundary routeKey={location.pathname}>
 *     <Outlet />
 *   </RouteErrorBoundary>
 */
class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Log so we can grep server-side / Sentry later.
    console.error('[RouteErrorBoundary]', error, info?.componentStack || '');
  }

  componentDidUpdate(prevProps) {
    // Reset when navigating to a different route — gives the user a clean slate.
    if (prevProps.routeKey !== this.props.routeKey && this.state.error) {
      this.setState({ error: null, info: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const msg = (this.state.error && this.state.error.message) || 'Unknown error';
    return (
      <div className="max-w-xl mx-auto py-12" data-testid="route-error-boundary">
        <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h2 className="font-heading text-lg text-white">Something went wrong on this page</h2>
              <p className="text-xs text-zinc-400 mt-1">
                The rest of the app is still working — head back to the dashboard or reload this page.
                Your work is safe; nothing was lost.
              </p>
              <details className="mt-3 text-xs text-zinc-500">
                <summary className="cursor-pointer text-zinc-400 hover:text-zinc-300">Show technical details</summary>
                <pre className="mt-2 p-2 bg-zinc-900 border border-zinc-800 rounded text-[10px] overflow-x-auto whitespace-pre-wrap text-zinc-400" data-testid="route-error-details">{msg}</pre>
              </details>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => window.location.reload()} className="bg-white text-[#0f0f10] hover:bg-zinc-200 font-semibold" data-testid="route-error-reload">
                  <RotateCw className="w-3.5 h-3.5 mr-2" /> Reload page
                </Button>
                <Button variant="outline" onClick={() => { window.location.href = '/'; }} className="border-zinc-700 text-zinc-300 hover:text-white" data-testid="route-error-home">
                  Go to dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
